import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AtSign, Bell, CheckCheck, MessageCircle, Share2, UserPlus } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { EmptyState } from '@/components/EmptyState';
import { Popover } from '@/components/ui/Popover';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { timeAgo, tr, trf } from '@/i18n';
import type { UserSummary } from '@/lib/types';

export interface AppNotification {
  id: string;
  type: 'mention' | 'reply' | 'invite' | 'board_shared' | 'comment';
  data: Record<string, unknown>;
  createdAt: number;
  readAt: number | null;
  actor: UserSummary | null;
  board: { id: string; name: string } | null;
}

export function useNotifications(refetchInterval = 30_000) {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ unread: number; notifications: AppNotification[] }>('/api/notifications'),
    refetchInterval,
  });
}

export function describeNotification(n: AppNotification): { icon: typeof Bell; text: string; href: string | null } {
  const who = n.actor?.name ?? tr("Кто-то");
  const board = n.board?.name ? `«${n.board.name}»` : tr("доске");
  const thread = typeof n.data.threadId === 'string' ? `?thread=${n.data.threadId}` : '';
  switch (n.type) {
    case 'mention':
      return { icon: AtSign, text: trf('{who} упомянул(а) вас в комментарии на {board}', { who, board }), href: n.board ? `/board/${n.board.id}${thread}` : null };
    case 'reply':
      return { icon: MessageCircle, text: trf('{who} ответил(а) в обсуждении на {board}', { who, board }), href: n.board ? `/board/${n.board.id}${thread}` : null };
    case 'comment':
      return { icon: MessageCircle, text: trf('{who} оставил(а) комментарий на {board}', { who, board }), href: n.board ? `/board/${n.board.id}${thread}` : null };
    case 'board_shared':
      return { icon: Share2, text: trf('{who} поделился(ась) доской {board}', { who, board }), href: n.board ? `/board/${n.board.id}` : null };
    case 'invite':
      return { icon: UserPlus, text: trf('{who} приглашает вас в «{ws}»', { who, ws: String(n.data.workspaceName ?? tr('воркспейс')) }), href: typeof n.data.url === 'string' ? n.data.url : null };
  }
}

export function NotificationItem({ n, onOpen }: { n: AppNotification; onOpen: (n: AppNotification) => void }) {
  const d = describeNotification(n);
  const Icon = d.icon;
  return (
    <button type="button" onClick={() => onOpen(n)} className={cn('flex w-full items-start gap-3 rounded-control p-2.5 text-left hover:bg-hover', !n.readAt && 'bg-accent-soft/50')}>
      <span className="relative">
        {n.actor ? <Avatar name={n.actor.name} src={n.actor.avatarUrl} seed={n.actor.id} size={32} /> : <span className="inline-flex size-8 items-center justify-center rounded-full bg-surface-2" />}
        <span className="absolute -right-1 -bottom-1 inline-flex size-[18px] items-center justify-center rounded-full bg-surface text-accent-fg shadow-card">
          <Icon className="size-3" />
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] leading-snug">{d.text}</span>
        {typeof n.data.excerpt === 'string' && <span className="mt-1 block truncate text-xs text-fg-muted">{n.data.excerpt}</span>}
        <span className="mt-1 block text-[11px] text-fg-subtle">{timeAgo(n.createdAt)}</span>
      </span>
      {!n.readAt && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" aria-label={tr("Не прочитано")} />}
    </button>
  );
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api('/api/notifications/read', { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** Колокольчик с количеством непрочитанных и всплывающим списком. */
export function NotificationsBell() {
  const { data } = useNotifications();
  const markRead = useMarkRead();
  const navigate = useNavigate();
  const unread = data?.unread ?? 0;
  const open = (n: AppNotification) => {
    if (!n.readAt) markRead.mutate({ ids: [n.id] });
    const href = describeNotification(n).href;
    if (href) navigate(href);
  };
  return (
    <Popover
      side="right"
      align="start"
      autoFocus
      className="w-[360px] p-1.5"
      trigger={
        <button type="button" aria-label={unread ? trf('Уведомления: {n} новых', { n: unread }) : tr("Уведомления")} className="relative inline-flex size-9 shrink-0 items-center justify-center rounded-control text-fg-muted hover:bg-hover hover:text-fg">
          <Bell className="size-[18px]" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 inline-flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-4 font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      }
    >
      <div className="flex items-center justify-between px-2 pt-1 pb-2">
        <span className="text-sm font-semibold">{tr("Уведомления")}</span>
        <div className="flex items-center gap-1">
          {unread > 0 && (
            <button type="button" onClick={() => markRead.mutate({ all: true })} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-hover hover:text-fg">
              <CheckCheck className="size-3.5" /> {tr("Прочитать все")}
            </button>
          )}
          <button type="button" onClick={() => navigate('/notifications')} className="rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-hover hover:text-fg">
            {tr("Все")}
          </button>
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {data?.notifications.length ? (
          data.notifications.slice(0, 15).map((n) => <NotificationItem key={n.id} n={n} onOpen={open} />)
        ) : (
          <div className="scale-90">
            <EmptyState variant="notifications" title={tr("Пока тихо")} text={tr("Упоминания, ответы и приглашения появятся здесь.")} />
          </div>
        )}
      </div>
    </Popover>
  );
}
